import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  AuthUser,
  getStoredUser,
  isAuthenticated,
  login as authLogin,
  logout as authLogout,
} from '../services/authService';

interface AuthContextValue {
  user: AuthUser | null;
  isLoggedIn: boolean;
  companyName: string;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser());

  useEffect(() => {
    if (!isAuthenticated()) {
      setUser(null);
    }
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const loggedInUser = await authLogin(username, password);
    setUser(loggedInUser);
  }, []);

  const logout = useCallback(() => {
    authLogout();
    setUser(null);
  }, []);

  const companyName = user?.company_name || 'LiquidBot';

  return (
    <AuthContext.Provider value={{ user, isLoggedIn: !!user, companyName, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
